/* eslint-disable no-unsafe-optional-chaining */
import { Tabs, Tab, Grid, Pagination } from "@mui/material";
import {
  fetchCompetitions,
  deleteCompetition,
  endCompetition,
  competitionActivate,
  removeFromFavorites,
} from "apis/compete";
import { useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { enqueueSnackbar } from "notistack";
import DashboardLayout from "utils/LayoutContainers/DashboardLayout";
import DashboardNavbar from "utils/Navbars/DashboardNavbar";
import Loader from "utils/Loader";
import CompetitionCard from "./components/CompetitionCard";
import RunCompetition from "./components/RunCompetition";
import SelectParticipants from "./components/SelectParticipants";

const COMPETITION_STATUS = {
  ACTIVE: "my_active_compete",
  DRAFTED: "draft",
  TEMPLATES: "my_fav_compete",
  CLOSED: "my_close_competition",
};

function Builder() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);
  const [payload, setPayload] = useState({
    pageNumber: 0,
    pageSize: 9,
    searchBy: "",
    type: COMPETITION_STATUS.ACTIVE,
  });
  const [closedCompetitionPayload, setClosedCompetitionPayload] = useState({
    pageNumber: 0,
    pageSize: 9,
    searchBy: "",
    type: COMPETITION_STATUS.CLOSED,
  });
  const [competitions, setCompetitions] = useState({ data: [], count: 0 });
  const [closedCompetition, setClosedCompetitions] = useState({ data: [], count: 0 });
  const [competition, setCompetition] = useState();
  const [isRunCompetitionModalOpen, setIsRunCompetitionModalOpen] = useState(false);
  const [activateCompetition, setActivateCompetition] = useState(false);
  const [isParticipantsModalOpen, setIsParticipantModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isClosedCompetitionLoading, setIsClosedCompetitionLoading] = useState(false);

  const handleChange = (event, newValue) => {
    if (newValue === 0) {
      setPayload((prevState) => ({
        ...prevState,
        pageNumber: 0,
        type: COMPETITION_STATUS.ACTIVE,
      }));
    } else if (newValue === 1) {
      setPayload((prevState) => ({
        ...prevState,
        pageNumber: 0,
        type: COMPETITION_STATUS.DRAFTED,
      }));
    } else {
      setPayload((prevState) => ({
        ...prevState,
        pageNumber: 0,
        type: COMPETITION_STATUS.TEMPLATES,
      }));
    }
    setSelectedTab(newValue);
  };

  const getCompetitions = async (_payload) => {
    try {
      if (_payload.type === COMPETITION_STATUS.CLOSED) {
        setIsClosedCompetitionLoading(true);
      } else {
        setIsLoading(true);
      }
      const response = await fetchCompetitions(_payload);
      if (response?.data?.success) {
        if (_payload.type === COMPETITION_STATUS.CLOSED) {
          setClosedCompetitions({
            data: response?.data?.data?.rows,
            count: response?.data?.data?.count,
          });
        } else {
          setCompetitions({ data: response?.data?.data?.rows, count: response?.data?.data?.count });
        }
      } else if (response?.response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
      if (_payload.type === COMPETITION_STATUS.CLOSED) {
        setIsClosedCompetitionLoading(false);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
      if (_payload.type === COMPETITION_STATUS.CLOSED) {
        setIsClosedCompetitionLoading(false);
      } else {
        setIsLoading(false);
      }
    }
  };

  const getDisplayNameOfCompetition = (tab) => {
    switch (tab) {
      case 0:
        return "active";
      case 1:
        return "drafted";
      case 2:
        return "favorite";

      default:
        return "active";
    }
  };

  useEffect(() => {
    getCompetitions(payload);
  }, [payload]);

  useEffect(() => {
    if (selectedTab === 0) {
      getCompetitions(closedCompetitionPayload);
    }
  }, [selectedTab, closedCompetitionPayload]);

  const activateCompetitionHandler = async (competitionPayload) => {
    const response = await competitionActivate(competitionPayload);
    try {
      if (response?.data?.success) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Success",
            text: response?.data?.message || "Competition successfully ended.",
          }),
          {
            variant: "custom",
          }
        );
        setPayload((prevState) => ({ ...prevState, pageNumber: 0 }));
      } else {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
        setPayload((prevState) => ({ ...prevState, pageNumber: 0 }));
      }
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.response.data.message || error.message || "Something went wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const handleCompetitionEnd = (competitionToEnd) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Are you sure you want to end this competition?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, End it!",
    })
      .then(async (result) => {
        if (result.isConfirmed) {
          const response = await endCompetition({ competitionId: competitionToEnd.id });
          try {
            if (response?.data?.success) {
              getCompetitions(closedCompetitionPayload);
              enqueueSnackbar(
                JSON.stringify({
                  icon: "success",
                  title: "Success",
                  text: response?.data?.message || "Competition successfully ended.",
                }),
                {
                  variant: "custom",
                }
              );
              setPayload((prevState) => ({ ...prevState, pageNumber: 0 }));
            } else {
              enqueueSnackbar(
                JSON.stringify({
                  icon: "error",
                  title: "Error",
                  text: response?.response?.data?.message || "Something Went Wrong",
                }),
                {
                  variant: "custom",
                }
              );
              setPayload((prevState) => ({ ...prevState, pageNumber: 0 }));
            }
          } catch (error) {
            enqueueSnackbar(
              JSON.stringify({
                icon: "error",
                title: "Error",
                text: error.response.data.message || error.message || "Something went wrong",
              }),
              {
                variant: "custom",
              }
            );
          }
        }
      })
      .catch();
  };

  const handleCompetitionDelete = (competitionToDelete) => {
    // inputOptions can be an object or Promise
    const inputOptions = new Promise((resolve) => {
      resolve({
        active: "Delete this active competition",
        all: "Delete this active and the following events",
      });
    });

    Swal.fire({
      title: "Are you sure?",
      text: `Are you sure you want to delete this competition?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: theme.palette.primaryRed.main,
      cancelButtonColor: theme.palette.gray[80],
      confirmButtonText: "Yes, Delete it!",
      ...(competitionToDelete?.repeatCompetition
        ? {
            input: "radio",
            inputOptions,
            inputValidator: (result) => (result ? null : "You need to select something!"),
          }
        : {}),
      customClass: {
        popup: "swal-vertical-radio",
      },
    })
      .then(async (result) => {
        if (result.isConfirmed) {
          if (competitionToDelete?.repeatCompetition && !result.value) {
            return enqueueSnackbar(
              JSON.stringify({
                icon: "error",
                title: "Error",
                text: "You need to select something",
              }),
              {
                variant: "custom",
              }
            );
          }
          setIsLoading(true);
          const response = await deleteCompetition(competitionToDelete.id, {
            type: competitionToDelete?.repeatCompetition ? result?.value : null,
          });
          try {
            if (response?.data?.success) {
              enqueueSnackbar(
                JSON.stringify({
                  icon: "success",
                  title: "Deleted",
                  text: response?.data?.message || "Competition successfully deleted.",
                }),
                {
                  variant: "custom",
                }
              );
              setPayload((prevState) => ({ ...prevState, pageNumber: 0 }));
            } else {
              enqueueSnackbar(
                JSON.stringify({
                  icon: "error",
                  title: "Error",
                  text: response?.response?.data?.message || "Something Went Wrong",
                }),
                {
                  variant: "custom",
                }
              );
              setPayload((prevState) => ({ ...prevState, pageNumber: 0 }));
            }
            setIsLoading(false);
          } catch (error) {
            setIsLoading(false);
            enqueueSnackbar(
              JSON.stringify({
                icon: "error",
                title: "Error",
                text: error.response.data.message || error.message || "Something went wrong",
              }),
              {
                variant: "custom",
              }
            );
          }
        }
        return true;
      })
      .catch();
  };

  const removeCompetitionFromFavorites = (competitionToRemoveFromFavorites) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Are you sure you want to delete this competition?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: theme.palette.primaryRed.main,
      cancelButtonColor: theme.palette.gray[80],
      confirmButtonText: "Yes, Delete it!",
    })
      .then(async (result) => {
        if (result.isConfirmed) {
          const response = await removeFromFavorites(competitionToRemoveFromFavorites.id);
          try {
            if (response?.data?.success) {
              enqueueSnackbar(
                JSON.stringify({
                  icon: "success",
                  title: "Deleted",
                  text: response?.data?.message || "Competition successfully deleted.",
                }),
                {
                  variant: "custom",
                }
              );
              setPayload((prevState) => ({ ...prevState, pageNumber: 0 }));
            } else {
              enqueueSnackbar(
                JSON.stringify({
                  icon: "error",
                  title: "Error",
                  text: response?.response?.data?.message || "Something Went Wrong",
                }),
                {
                  variant: "custom",
                }
              );
              setPayload((prevState) => ({ ...prevState, pageNumber: 0 }));
            }
          } catch (error) {
            enqueueSnackbar(
              JSON.stringify({
                icon: "error",
                title: "Error",
                text: error.response.data.message || error.message || "Something went wrong",
              }),
              {
                variant: "custom",
              }
            );
          }
        }
      })
      .catch();
  };

  const handleCompetitionActivate = (competitionToActivate) => {
    if (
      moment
        .tz(competitionToActivate.startDatetime, competitionToActivate.timezone)
        .isBefore(moment.tz(undefined, competitionToActivate.timezone))
    ) {
      setActivateCompetition(true);
      setCompetition(competitionToActivate);
      Swal.fire({
        title: "Activate Competition",
        text: `Competition start time is passed, Please change the start time.`,
        icon: "info",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Change Start Time!",
      }).then((result) => {
        if (result.isConfirmed) {
          setIsRunCompetitionModalOpen(true);
        } else {
          setActivateCompetition(false);
          setCompetition();
        }
      });
    } else {
      Swal.fire({
        title: "Activate Competition",
        text: `Do you want to activate this competition?`,
        icon: "info",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes!",
      }).then((result) => {
        if (result.isConfirmed) {
          activateCompetitionHandler({
            competitionId: competitionToActivate.id,
            timezone: competitionActivate.timezone,
          });
        }
      });
    }
  };

  return (
    <DashboardLayout>
      {/* header */}
      <DashboardNavbar />
      <MDBox sx={{ position: "relative" }}>
        <Loader loading={isLoading || isClosedCompetitionLoading} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={9}>
            <MDBox minWidth={{ xs: "100%", md: "25rem" }} width={{ xs: "100%", md: "60%" }}>
              <Tabs value={selectedTab} onChange={handleChange}>
                <Tab
                  id="monthly"
                  label={
                    <MDBox py={0.5} px={2} color="inherit">
                      My Competitions
                    </MDBox>
                  }
                />
                <Tab
                  id="annual"
                  label={
                    <MDBox py={0.5} px={2} color="inherit">
                      Draft
                    </MDBox>
                  }
                />
                <Tab
                  id="annual"
                  label={
                    <MDBox py={0.5} px={2} color="inherit">
                      My Competition Templates
                    </MDBox>
                  }
                />
              </Tabs>
            </MDBox>
          </Grid>
          <Grid
            item
            xs={12}
            sm={12}
            md={3}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
          >
            <MDButton
              type="submit"
              color="primaryRed"
              onClick={() => navigate("/compete/builder/add")}
            >
              + Add new Competition
            </MDButton>
          </Grid>
        </Grid>

        <MDBox mt={2}>
          {selectedTab === 0 && <MDTypography variant="h4">Active</MDTypography>}
          <MDBox
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              "& .MuiInputLabel-formControl": { lineHeight: "15px" },
            }}
            mt={1}
            mb={2}
          >
            <MDBox pr={1}>
              <MDInput
                label="Search"
                value={payload.searchBy}
                onChange={(e) =>
                  setPayload((prevState) => ({
                    ...prevState,
                    pageNumber: 0,
                    searchBy: e.target.value,
                  }))
                }
              />
            </MDBox>
          </MDBox>

          <Grid container spacing={4}>
            {competitions?.data.length === 0 && !isLoading ? (
              <Grid container spacing={4}>
                <Grid item>
                  <MDBox m={4}>
                    <MDTypography sx={{ mt: 5 }}>
                      You have no {getDisplayNameOfCompetition(selectedTab)} competitions
                    </MDTypography>
                  </MDBox>
                </Grid>
              </Grid>
            ) : (
              competitions?.data?.map((item) => (
                <Grid item xs={12} sm={12} md={4} lg={4} key={item.id}>
                  <CompetitionCard
                    showActions
                    competition={item}
                    selectedTab={selectedTab}
                    setCompetition={setCompetition}
                    handleCompetitionEnd={handleCompetitionEnd}
                    handleCompetitionDelete={handleCompetitionDelete}
                    setIsRunCompetitionModalOpen={setIsRunCompetitionModalOpen}
                    setIsParticipantModalOpen={setIsParticipantModalOpen}
                    handleCompetitionActivate={handleCompetitionActivate}
                    removeCompetitionFromFavorites={removeCompetitionFromFavorites}
                  />
                </Grid>
              ))
            )}
          </Grid>
          {competitions?.count > payload.pageSize && (
            <MDBox
              mt={2}
              sx={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Pagination
                count={Math.ceil(competitions?.count / payload.pageSize)}
                onChange={(_, page) =>
                  setPayload((prevState) => ({ ...prevState, pageNumber: page - 1 }))
                }
                page={payload.pageNumber + 1}
              />
            </MDBox>
          )}
        </MDBox>

        {/* CLOSED */}
        {selectedTab === 0 && (
          <MDBox mt={4}>
            <MDTypography variant="h4">Closed</MDTypography>
            <MDBox
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                "& .MuiInputLabel-formControl": { lineHeight: "15px" },
              }}
              mt={1}
              mb={2}
            >
              <MDBox pr={1}>
                <MDInput
                  label="Search"
                  value={closedCompetition.searchBy}
                  onChange={(e) =>
                    setClosedCompetitionPayload((prevState) => ({
                      ...prevState,
                      pageNumber: 0,
                      searchBy: e.target.value,
                    }))
                  }
                />
              </MDBox>
            </MDBox>

            <Grid container spacing={4}>
              {closedCompetition?.data.length === 0 && !isClosedCompetitionLoading ? (
                <Grid container spacing={4}>
                  <Grid item>
                    <MDBox m={4}>
                      <MDTypography sx={{ mt: 5 }}>You have no closed competitions</MDTypography>
                    </MDBox>
                  </Grid>
                </Grid>
              ) : (
                closedCompetition?.data?.map((item) => (
                  <Grid item xs={12} sm={12} md={4} lg={4} key={item.id}>
                    <CompetitionCard
                      competition={item}
                      showActions={false}
                      selectedTab={selectedTab}
                      setCompetition={setCompetition}
                      handleCompetitionEnd={handleCompetitionEnd}
                      handleCompetitionDelete={handleCompetitionDelete}
                      setIsRunCompetitionModalOpen={setIsRunCompetitionModalOpen}
                      setIsParticipantModalOpen={setIsParticipantModalOpen}
                      handleCompetitionActivate={handleCompetitionActivate}
                      removeCompetitionFromFavorites={removeCompetitionFromFavorites}
                    />
                  </Grid>
                ))
              )}
            </Grid>
            {closedCompetition?.count > closedCompetitionPayload.pageSize && (
              <MDBox
                mt={2}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Pagination
                  count={Math.ceil(closedCompetition?.count / closedCompetitionPayload.pageSize)}
                  onChange={(_, page) =>
                    setClosedCompetitionPayload((prevState) => ({
                      ...prevState,
                      pageNumber: page - 1,
                    }))
                  }
                  page={closedCompetitionPayload.pageNumber + 1}
                />
              </MDBox>
            )}
          </MDBox>
        )}
      </MDBox>

      <RunCompetition
        open={isRunCompetitionModalOpen}
        setOpen={setIsRunCompetitionModalOpen}
        competition={competition}
        setCompetition={setCompetition}
        activateCompetition={activateCompetition}
        setActivateCompetition={setActivateCompetition}
        handleCompetitionActivate={activateCompetitionHandler}
        setPayload={setPayload}
      />
      <SelectParticipants
        open={isParticipantsModalOpen}
        setOpen={setIsParticipantModalOpen}
        competition={competition}
        setCompetition={setCompetition}
      />
    </DashboardLayout>
  );
}

export default Builder;
